import React, {useEffect, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { validateEmail, validatePassword, getPasswordStrength } from '../../utils/validators';
import { AlertCircle, CheckCircle, Loader, Eye, EyeOff } from 'lucide-react';
import logo from "../../assets/images/logo.png";

const Register = () => {
    const navigate = useNavigate();
    const { register } = useAuth();

    const EURO_COUNTRIES = [
        'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI',
        'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU',
        'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
    ];

    const getCountryFromIP = async () => {
        const cached = localStorage.getItem('country_code');
        if (cached) return cached;

        try {
            const res = await fetch('https://ipapi.co/json/');
            const data = await res.json();
            const code = data.country_code || 'US';
            localStorage.setItem('country_code', code);
            return code;
        } catch {
            return 'US';
        }
    };

    const getCurrencyByCountry = (countryCode) => {
        if (countryCode === 'GB') return 'GBP';
        if (countryCode === 'NG') return 'NGN';
        if (countryCode === 'MY') return 'MYR';
        if (EURO_COUNTRIES.includes(countryCode)) return 'EUR';
        return 'USD';
    };

    const [countryCode, setCountryCode] = useState('US');
    const [currency, setCurrency] = useState('USD');

    // ✅ RUNS ONCE
    useEffect(() => {
        const initCountry = async () => {
            const code = await getCountryFromIP();
            setCountryCode(code);
            setCurrency(getCurrencyByCountry(code));
        };

        initCountry();
    }, []);


    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        user_type: 'parent',
        country_code: countryCode,
        currency: currency
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Check password strength
        if (name === 'password') {
            setPasswordStrength(getPasswordStrength(value));
        }

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.full_name.trim()) {
            newErrors.full_name = 'Full name is required';
        }

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Invalid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (!validatePassword(formData.password)) {
            newErrors.password = 'Password must be at least 6 characters and not all numbers';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (!validate()) return;

        try {
            setLoading(true);
            await register({
                full_name: formData.full_name,
                email: formData.email,
                password: formData.password,
                user_type: formData.user_type,
                country_code: countryCode,
                currency: currency,
                auth_provider: 'local'
            });

            // Redirect to appropriate dashboard
            if (formData.user_type === 'parent') {
                navigate('/parent/dashboard');
            }
            // else if (formData.user_type === 'student') {
            //     navigate('/student/dashboard');
            // }
        } catch (error) {
            setApiError(error.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-10">
                    <Link to="/" className="inline-flex justify-center">
                        <img
                            src={logo}
                            alt="STEM4KIDDO"
                            className="h-20 md:h-24 w-auto mx-auto"
                        />
                    </Link>
                </div>

                {/* Register Form */}
                <div className="card">
                    <h2 className="text-2xl font-bold mb-6">Sign Up</h2>

                    {apiError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{apiError}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium mb-2">Full Name</label>
                            <input
                                type="text"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                className={`input-field ${errors.full_name ? 'border-red-500' : ''}`}
                                placeholder="John Doe"
                            />
                            {errors.full_name && (
                                <p className="text-red-600 text-sm mt-1">{errors.full_name}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                                placeholder="your.email@example.com"
                            />
                            {errors.email && (
                                <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">I am a...</label>
                            <select
                                name="user_type"
                                value={formData.user_type}
                                onChange={handleChange}
                                className="input-field"
                            >
                                <option value="parent">Parent/Guardian</option>
                                {/*<option value="teacher">Teacher</option>*/}
                                {/*<option value="student">Student</option>*/}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>

                            {passwordStrength && formData.password && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all bg-${passwordStrength.color}-500`}
                                                style={{ width: passwordStrength.level === 'weak' ? '33%' : passwordStrength.level === 'medium' ? '66%' : '100%' }}
                                            />
                                        </div>
                                        <span className={`text-xs font-medium text-${passwordStrength.color}-600`}>
                      {passwordStrength.level}
                    </span>
                                    </div>
                                </div>
                            )}

                            {errors.password && (
                                <p className="text-red-600 text-sm mt-1">{errors.password}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Confirm Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
                                placeholder="••••••••"
                            />
                            {errors.confirmPassword && (
                                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>
                            )}
                        </div>

                        <div className="flex items-start">
                            <input type="checkbox" required className="mt-1 rounded mr-2" />
                            <span className="text-sm text-gray-600">
                I have read and agree to the{' '}
                {/*                <Link to="/terms" className="text-purple-600 hover:text-purple-700">*/}
                {/*  Terms of Service*/}
                {/*</Link>{' '}*/}

                                <Link to="/privacy" className="text-purple-600 hover:text-purple-700">
                  Privacy Policy
                </Link>
              </span>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-gray-600">
                            Already have an account?{' '}
                            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">
                                Login
                            </Link>
                        </p>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500">Powered by Fancybox LDA</p>
                </div>

            </div>
        </div>
    );
};

export default Register;